/**
 * Header the Sidekick auth proxy reads to scope a request to another
 * organization. The platform's own organization header, matching
 * `BD_ORGANIZATION_HEADER_NAME` in `fleet-management`'s `api.constants`
 * and the literal the generated `rtk-clients` send.
 *
 * Declared here rather than imported because this package depends on
 * neither of those, and no shared package owns the name.
 */
export const BD_ORGANIZATION_HEADER_NAME = 'x-bd-organization';
export const SIDEKICK_ACTING_ORGANIZATION_OVERRIDE_HEADER_NAME =
  'x-sidekick-acting-organization-override';

/** Organization scope captured before an asynchronous Sidekick operation. */
export type SidekickActingOrganizationScope = {
  actingOrganizationId: string | null;
};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

let actingOrganizationId: string | undefined;

const normalizeActingOrganizationId = (
  organizationId: string | null | undefined
): string | undefined => {
  const normalized = organizationId?.trim().toLowerCase();

  return normalized !== undefined && UUID_PATTERN.test(normalized)
    ? normalized
    : undefined;
};

/**
 * Records the organization the signed-in user is currently acting as,
 * or clears it when they return to their own.
 *
 * Module-level rather than context because every Sidekick transport
 * needs it and none reads it from inside the component tree: the RTK
 * Query client's `prepareHeaders`, the SSE/sync `fetchWithSidekickAuth`
 * and a host app's own client (`mf-insights`'s `baseQueryWithAuth`, which
 * the M1-off bridges select) are plain functions, the same reason the
 * bearer token is resolved from `refreshTokenHandler` rather than a hook.
 * `SidekickLayoutShell` and `SidekickDockShell` own the writes via
 * `useSidekickActingOrganization`, so hosts pass a prop.
 *
 * Anything that is not a UUID is refused rather than stored, blank
 * included. The proxy reads this header only when the caller's token
 * carries no organization of its own, and rejects a blank or malformed
 * value outright, so storing one would turn an advisor session that
 * should simply send no header into a refused request.
 */
export const setSidekickActingOrganizationId = (
  organizationId: string | undefined
): void => {
  actingOrganizationId = normalizeActingOrganizationId(organizationId);
};

/**
 * Reads the acting organization at request time rather than at render
 * time, so a turn issued before the user switched organizations keeps
 * the organization it was issued for.
 */
export const getSidekickActingOrganizationId = (): string | undefined =>
  actingOrganizationId;

/**
 * Captures the current acting organization, including an explicit home-org
 * scope when no acting organization is set.
 * @returns Immutable request scope for retries and cleanup
 */
export const captureSidekickActingOrganizationScope =
  (): SidekickActingOrganizationScope => ({
    actingOrganizationId: getSidekickActingOrganizationId() ?? null,
  });

/**
 * Builds the internal header used to carry a captured organization through
 * RTK Query's async header preparation.
 * @param scope - Organization scope captured when the operation started
 * @returns Internal headers removed before the request reaches the network
 */
export const getSidekickActingOrganizationScopeHeaders = (
  scope: SidekickActingOrganizationScope
): Record<string, string> => ({
  [SIDEKICK_ACTING_ORGANIZATION_OVERRIDE_HEADER_NAME]:
    scope.actingOrganizationId ?? '',
});

/**
 * Returns `headers` with the acting organization in scope, dropping any
 * the caller supplied.
 *
 * Removes before it adds, so this module stays the only authority on the
 * header: a call site that set it directly would otherwise widen its own
 * scope, and would keep doing so once the user returned to their own
 * organization and there was no value here to overwrite it with.
 *
 * Absent rather than the home organization id when the user is acting as
 * themselves, because a non-blank `extension_organizationId` claim
 * outranks this header at the proxy, so sending it would be discarded.
 *
 * The comparison is case-insensitive because HTTP header names are, and
 * `fetch` merges a plain object's keys verbatim, so a caller writing
 * `X-BD-Organization` (as the test-data scripts do) beside our lowercase
 * one reaches the proxy as two values of a single header.
 */
export const withActingOrganizationHeader = (
  headers: Record<string, string> | undefined,
  organizationId: string | null | undefined = getSidekickActingOrganizationId()
): Record<string, string> => {
  const retained = Object.fromEntries(
    Object.entries(headers ?? {}).filter(
      ([name]) => name.toLowerCase() !== BD_ORGANIZATION_HEADER_NAME
    )
  );
  const normalizedOrganizationId =
    normalizeActingOrganizationId(organizationId);

  return normalizedOrganizationId === undefined
    ? retained
    : { ...retained, [BD_ORGANIZATION_HEADER_NAME]: normalizedOrganizationId };
};

/**
 * The same policy against the mutable `Headers` that RTK Query hands to
 * `prepareHeaders`. `Headers.delete` is already case-insensitive, so this
 * needs no folding of its own.
 */
export const setActingOrganizationHeader = (
  headers: Headers,
  organizationId = getSidekickActingOrganizationId()
): void => {
  const normalizedOrganizationId =
    normalizeActingOrganizationId(organizationId);

  headers.delete(BD_ORGANIZATION_HEADER_NAME);
  if (normalizedOrganizationId !== undefined) {
    headers.set(BD_ORGANIZATION_HEADER_NAME, normalizedOrganizationId);
  }
};
